import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ 
  className, 
  children, 
  hideCloseButton = false,
  preventClose = false,
  onCloseClick,
  ...props 
}, ref) => {
  // Prevent swipe gestures when modal is open
  React.useEffect(() => {
    if (preventClose) {
      // Add class to body to prevent swipe navigation
      document.body.classList.add('modal-no-swipe');
      document.body.style.overscrollBehaviorX = 'none';
      
      // Prevent horizontal touch swipes
      const preventSwipe = (e) => {
        if (e.touches && e.touches.length === 1) {
          const touch = e.touches[0];
          const startX = touch.clientX;
          
          const handleMove = (moveEvent) => {
            const moveX = moveEvent.touches[0].clientX;
            const diffX = Math.abs(moveX - startX);
            const diffY = Math.abs(moveEvent.touches[0].clientY - touch.clientY);
            
            // If horizontal movement is greater than vertical, it's a swipe - prevent it
            if (diffX > diffY && diffX > 10) {
              moveEvent.preventDefault();
            }
          };
          
          document.addEventListener('touchmove', handleMove, { passive: false });
          
          const cleanup = () => {
            document.removeEventListener('touchmove', handleMove);
          };
          
          document.addEventListener('touchend', cleanup, { once: true });
          document.addEventListener('touchcancel', cleanup, { once: true });
        }
      };
      
      document.addEventListener('touchstart', preventSwipe, { passive: true });
      
      return () => {
        document.body.classList.remove('modal-no-swipe');
        document.body.style.overscrollBehaviorX = '';
        document.removeEventListener('touchstart', preventSwipe);
      };
    }
  }, [preventClose]);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-gray-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          preventClose && "touch-pan-y",
          className
        )}
        style={preventClose ? { touchAction: 'pan-y', overscrollBehavior: 'none' } : undefined}
        onPointerDownOutside={preventClose ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={preventClose ? (e) => e.preventDefault() : undefined}
        onInteractOutside={preventClose ? (e) => e.preventDefault() : undefined}
        {...props}>
        {children}
        {!hideCloseButton && (
          onCloseClick ? (
            <button
              type="button"
              onClick={onCloseClick}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          ) : (
            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-gray-900", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
